import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getAssistantById } from '../../services/assistantConfigService';
import { useVapi } from '../../contexts/VoiceCallContext';
import { motion, AnimatePresence } from 'framer-motion';
import VoiceCallWidget from '../VoiceCall/VoiceCallWidget';
import { hasReachedTrialLimitAsync, incrementTrialCallCount, getRemainingTrialCalls } from '../../services/trialLimitService';
import logo from '../../assets/images/LifeCoachAssist-logo-02.png';
import toast from 'react-hot-toast';

const TrialCallPage = () => {
  const navigate = useNavigate();
  const { isCallActive } = useVapi();
  const [trialAssistant, setTrialAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callCompleted, setCallCompleted] = useState(false);
  const [hasStartedCall, setHasStartedCall] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const init = async () => {
      // Check trial limit before loading
      const limitReached = await hasReachedTrialLimitAsync();
      if (limitReached) {
        setError('You have reached the trial call limit. Please sign up to continue.');
        setLoading(false);
        return;
      }
      
      loadTrialAssistant();
    };
    
    init();
  }, []);

  // Track when call starts and monitor when it ends
  useEffect(() => {
    const handleCallState = async () => {
      if (isCallActive && !hasStartedCall) {
        setHasStartedCall(true);
        // Increment trial counter when call starts
        incrementTrialCallCount();
        console.log('Trial call started, counter incremented');
      }
      
      if (!isCallActive && hasStartedCall && !callCompleted && trialAssistant) {
        // Call was active but is now ended
        setCallCompleted(true);
        
        // Check if they've reached the limit
        const limitReached = await hasReachedTrialLimitAsync();
        if (limitReached) {
          toast('Trial limit reached. Sign up to continue!', {
            icon: '🎯',
            duration: 5000
          });
        } else {
          const remaining = await getRemainingTrialCalls();
          toast.success(`Thanks for trying! ${remaining} trial call${remaining === 1 ? '' : 's'} remaining.`);
        }
      }
    };
    
    handleCallState();
  }, [isCallActive, hasStartedCall, callCompleted, trialAssistant]);

  const loadTrialAssistant = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the database function to get trial assistant
      const { data: assistantId, error: rpcError } = await supabase
        .rpc('get_trial_assistant');

      if (rpcError) {
        console.error('Error getting trial assistant:', rpcError);
        throw new Error(`Failed to load trial assistant: ${rpcError.message}`);
      }

      if (!assistantId) {
        throw new Error('No trial assistant configured. Please contact support.');
      }

      // Fetch the full assistant configuration
      const { data: assistant, error: assistantError } = await getAssistantById(assistantId);

      if (assistantError || !assistant) {
        console.error('Error fetching assistant:', assistantError);
        throw new Error('Failed to load assistant configuration');
      }

      setTrialAssistant(assistant);
      console.log('✅ Trial assistant loaded:', assistant.name);
    } catch (err) {
      console.error('Error loading trial assistant:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCallEnd = () => {
    // Navigate back to signup page after call ends
    toast.success('Thanks for trying Life Coach Assist!');
    navigate('/signup-options');
  };

  const handleBackToSignup = () => {
    navigate('/signup-options');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center mt-4 pt-safe">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trial assistant...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isLimitReached = error.includes('trial call limit') || error.includes('Trial limit');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 mt-4 pt-safe">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <img src={logo} alt="Life Coach Assist" className="h-16 mx-auto mb-6" />
          <div className="mb-6">
            <div className={`text-5xl mb-4 ${isLimitReached ? 'text-yellow-500' : 'text-red-500'}`}>
              {isLimitReached ? '🎯' : '⚠️'}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isLimitReached ? 'Trial Limit Reached' : 'Unable to Load Trial'}
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            {isLimitReached && (
              <p className="text-sm text-gray-500 mb-4">
                Sign up now to get unlimited access to Life Coach Assist!
              </p>
            )}
          </div>
          <div className="space-y-3">
            <button
              onClick={handleBackToSignup}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              {isLimitReached ? 'Sign Up Now' : 'Back to Signup'}
            </button>
            {!isLimitReached && (
              <button
                onClick={handleBackToSignup}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Back to Options
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 mt-4 pt-safe">
      <div className="container mx-auto px-4 py-8">
        {/* Header - Only show before any call activity */}
        <AnimatePresence>
          {!hasStartedCall && (
            <motion.div
              initial={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="text-center mb-8 overflow-hidden"
            >
              <img src={logo} alt="Life Coach Assist" className="h-16 mx-auto mb-4" />
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Test Life Coach Assist
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Experience our AI-powered coaching assistant.	See how a client would experience this by clicking the TALK button below to start up to three short trial contacts.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Voice Call Widget - Slides up when call starts */}
        <motion.div
          animate={{
            marginTop: isCallActive || callCompleted ? 0 : 0
          }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {!callCompleted ? (
              <>
                {/* Voice Widget */}
                {trialAssistant && (
                  <VoiceCallWidget
                    selectedAssistant={trialAssistant}
                    isFloating={false}
                    hideAssistantInfo={true}
                  />
                )}

                {/* Info Box */}
                {!isCallActive && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> This is a demonstration. To access all platform features, or to find out more, click <b>Back to Sign Up Options</b> to EXPLORE IT, RECOMMEND IT, or if you are a coach, to USE IT.
                    </p>
                  </div>
                )}

                {/* Back Button */}
                <div className="mt-6 text-center">
                  <button
                    onClick={handleBackToSignup}
                    className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-2"
                  >
                    <span>←</span> Back to Signup Options
                  </button>
                </div>
              </>
            ) : (
              /* Call Completed Message */
              <div className="text-center py-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Thanks for testing Life Coach Assist
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  If you are a coach and want to use it, click a button below to either Explore It more or apply to USE IT.
                </p>
                <div className="space-y-4">
                  <button
                    onClick={() => window.open('https://www.lifecoachassist.ai', '_blank')}
                    className="w-full bg-indigo-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-indigo-700 transition-colors"
                  >
                    Explore It
                  </button>
                  <Link
                    to="/signup-options?form=use-it"
                    className="block w-full bg-yellow-400 text-gray-900 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors"
                  >
                    Use It
                  </Link>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TrialCallPage;
